import { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { 
  Crown, Sparkles, User, Building, Plus, Pencil, Trash2, 
  Save, RefreshCw, Check, X, DollarSign, Settings2, 
  ToggleLeft, ToggleRight, GripVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/modal-dialog';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const ICONS = {
  user: User,
  crown: Crown,
  sparkles: Sparkles,
  building: Building,
};

const TierManager = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      
      const res = await axios.get(`${API_BASE}/api/admin/tiers`, {
        withCredentials: true
      });
      setTiers(res.data.tiers || []);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
      toast.error('Failed to load tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTier = async (tierData) => {
    try {
      
      
      if (editingTier) {
        // Update existing
        await axios.put(`${API_BASE}/api/admin/tiers/${editingTier.key}`, tierData, {
          withCredentials: true
        });
        toast.success('Tier updated successfully');
      } else {
        // Create new
        await axios.post(`${API_BASE}/api/admin/tiers`, tierData, {
          withCredentials: true
        });
        toast.success('Tier created successfully');
      }
      
      setEditingTier(null);
      setShowCreateModal(false);
      fetchTiers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tier');
    }
  };

  const handleDeleteTier = async (tierKey) => {
    try {
      
      await axios.delete(`${API_BASE}/api/admin/tiers/${tierKey}`, {
        withCredentials: true
      });
      toast.success('Tier deactivated');
      setConfirmDelete(null);
      fetchTiers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tier');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      
      await axios.post(`${API_BASE}/api/admin/tiers/seed`, {}, {
        withCredentials: true
      });
      toast.success('Default tiers restored');
      setConfirmReset(false);
      fetchTiers();
    } catch (error) {
      toast.error('Failed to seed defaults');
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
          <Crown className="w-6 h-6 text-[#F59E0B]" />
          <h2 className="text-xl font-bold text-white">Membership Tiers</h2>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setConfirmReset(true)}
            variant="outline"
            className="border-gray-600 text-gray-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset Defaults
          </Button>
          <Button
            onClick={() => {
              setEditingTier(null);
              setShowCreateModal(true);
            }}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Tier
          </Button>
        </div>
      </div>

      <p className="text-gray-400 text-sm">
        Manage membership tiers, pricing, and features. Changes affect new users and tier upgrades.
      </p>

      {/* Tiers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiers.map(tier => {
          const IconComponent = ICONS[tier.badge_icon] || Sparkles;
          return (
            <div
              key={tier.key}
              className={`p-5 rounded-xl border transition-all ${
                tier.is_active 
                  ? 'border-gray-700 bg-gray-800/50' 
                  : 'border-red-900/30 bg-red-900/10 opacity-60'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${tier.badge_color}20` }}
                  >
                    <IconComponent className="w-5 h-5" style={{ color: tier.badge_color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{tier.name}</h3>
                    <span className="text-xs text-gray-500">{tier.key}</span>
                  </div>
                </div>
                {tier.is_default && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">
                    Default
                  </span>
                )}
              </div>

              {/* Pricing */}
              <div className="mb-4">
                {tier.price_monthly > 0 ? (
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-white">${tier.price_monthly}</span>
                    <span className="text-gray-400 text-sm">/mo</span>
                  </div>
                ) : (
                  <span className="text-lg font-semibold text-green-400">Free</span>
                )}
                {tier.price_yearly > 0 && (
                  <p className="text-xs text-gray-500">${tier.price_yearly}/year</p>
                )}
              </div>

              {/* Description */}
              <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                {tier.description || 'No description'}
              </p>

              {/* Features count */}
              <div className="text-xs text-gray-500 mb-4">
                {tier.features?.length || 0} features configured
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-gray-600"
                  onClick={() => {
                    setEditingTier(tier);
                    setShowCreateModal(true);
                  }}
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                {!tier.is_default && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-600 text-red-400 hover:bg-red-600/20"
                    onClick={() => setConfirmDelete(tier)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <TierEditModal
          tier={editingTier}
          onSave={handleSaveTier}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTier(null);
          }}
        />
      )}
    </div>
  );
  };

  return (
    <>
      {renderContent()}
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => handleDeleteTier(confirmDelete?.key)}
        title="Deactivate Tier"
        message={`Are you sure you want to deactivate the "${confirmDelete?.name}" tier? Users with this tier will be moved to Free.`}
        confirmText="Deactivate"
        confirmVariant="destructive"
      />
      
      {/* Reset Defaults Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={handleSeedDefaults}
        title="Reset to Defaults"
        message="This will reset all tiers to their default configuration. Any custom tiers will be removed. Are you sure?"
        confirmText="Reset All"
        confirmVariant="destructive"
      />
    </>
  );
};


// Tier Edit Modal Component
const TierEditModal = ({ tier, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    key: tier?.key || '',
    name: tier?.name || '',
    description: tier?.description || '',
    price_monthly: tier?.price_monthly || 0,
    price_yearly: tier?.price_yearly || 0,
    currency: tier?.currency || 'USD',
    is_default: tier?.is_default || false,
    is_active: tier?.is_active ?? true,
    sort_order: tier?.sort_order || 0,
    badge_color: tier?.badge_color || '#df1283',
    badge_icon: tier?.badge_icon || 'sparkles',
    features: tier?.features || [],
  });

  const [newFeature, setNewFeature] = useState({ key: '', name: '', limit: null, enabled: true });

  const handleAddFeature = () => {
    if (!newFeature.key || !newFeature.name) return;
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, { ...newFeature }]
    }));
    setNewFeature({ key: '', name: '', limit: null, enabled: true });
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-2xl bg-gray-900 border border-gray-700 rounded-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {tier ? `Edit "${tier.name}"` : 'Create New Tier'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Key (unique)</label>
              <Input
                value={formData.key}
                onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                placeholder="premium"
                className="bg-gray-800 border-gray-700"
                disabled={!!tier}
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Display Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Premium"
                className="bg-gray-800 border-gray-700"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Full access to all features..."
              className="bg-gray-800 border-gray-700"
            />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Monthly Price
              </label>
              <Input
                type="number"
                step="0.01"
                value={formData.price_monthly}
                onChange={(e) => setFormData(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Yearly Price</label>
              <Input
                type="number"
                step="0.01"
                value={formData.price_yearly}
                onChange={(e) => setFormData(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) || 0 }))}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Sort Order</label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                className="bg-gray-800 border-gray-700"
              />
            </div>
          </div>

          {/* Badge styling */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Badge Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.badge_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, badge_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-600"
                />
                <Input
                  value={formData.badge_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, badge_color: e.target.value }))}
                  className="bg-gray-800 border-gray-700 flex-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Badge Icon</label>
              <div className="flex gap-2">
                {['user', 'crown', 'sparkles', 'building'].map(icon => {
                  const IconComp = ICONS[icon];
                  return (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, badge_icon: icon }))}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.badge_icon === icon
                          ? 'border-[#df1283] bg-[#df1283]/20'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <IconComp className="w-5 h-5" style={{ color: formData.badge_color }} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-300">Default Tier</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-300">Active</span>
            </label>
          </div>

          {/* Features */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Features</label>
            <div className="space-y-2 mb-3">
              {formData.features.map((feature, idx) => (
                <div key={feature.key || `feature-${idx}`} className="flex items-center gap-2 p-2 bg-gray-800 rounded-lg">
                  <span className="flex-1 text-sm text-white">{feature.name}</span>
                  <span className="text-xs text-gray-500">{feature.key}</span>
                  <span className="text-xs text-gray-400">
                    {feature.limit === null ? 'Unlimited' : feature.limit === 0 ? 'Disabled' : `${feature.limit} uses`}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(idx)}
                    className="p-1 text-red-400 hover:bg-red-600/20 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Add feature */}
            <div className="flex gap-2">
              <Input
                value={newFeature.key}
                onChange={(e) => setNewFeature(prev => ({ ...prev, key: e.target.value }))}
                placeholder="feature_key"
                className="bg-gray-800 border-gray-700 w-32"
              />
              <Input
                value={newFeature.name}
                onChange={(e) => setNewFeature(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Feature Name"
                className="bg-gray-800 border-gray-700 flex-1"
              />
              <Input
                type="number"
                value={newFeature.limit === null ? '' : newFeature.limit}
                onChange={(e) => setNewFeature(prev => ({ 
                  ...prev, 
                  limit: e.target.value === '' ? null : parseInt(e.target.value) 
                }))}
                placeholder="Limit (empty=∞)"
                className="bg-gray-800 border-gray-700 w-28"
              />
              <Button type="button" onClick={handleAddFeature} className="bg-gray-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="border-gray-600">
            Cancel
          </Button>
          <Button
            onClick={(e) => handleSubmit(e)}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
          >
            <Save className="w-4 h-4 mr-2" />
            {tier ? 'Update Tier' : 'Create Tier'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TierManager;
